import { useEffect, useMemo } from 'react';

import { useQuery } from '@zenbusiness/application-commons-graphql';

import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import { useLogError } from '#client/hooks/useLogError';
import useToastContext from '#client/hooks/useToastContext';
import useUnitAccountId from '#client/hooks/useUnitAccountId';
import { GET_PARTNER_ACCOUNT_LIMITS_QUERY } from '#client/queries';

export default function useUnitAccountLimits() {
  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const { unitAccountId } = useUnitAccountId();
  const { createCommonErrorToast } = useToastContext();

  const { error, errors, loading, data, refetch } = useQuery(GET_PARTNER_ACCOUNT_LIMITS_QUERY, {
    variables: {
      input: {
        businessUuid: businessEntityUuid,
        partnerAccountId: unitAccountId
      }
    },
    skip: !unitAccountId || !businessEntityUuid,
    noThrow: true,
    skipCache: true
  });

  useLogError(errors, error);

  // Calculate remaining ACH limits, ensuring values are never negative
  const remains = useMemo(() => {
    if (data) {
      const { achLimits, achTotalsDaily, achTotalsMonthly } = data.partnerAccountLimits;
      return {
        dailyDebitRemain: Math.max(0, achLimits.dailyDebit - achTotalsDaily.debits),
        monthlyDebitRemain: Math.max(0, achLimits.monthlyDebit - achTotalsMonthly.debits),
        dailyCreditRemain: Math.max(0, achLimits.dailyCredit - achTotalsDaily.credits),
        monthlyCreditRemain: Math.max(0, achLimits.monthlyCredit - achTotalsMonthly.credits)
      };
    }
    return {};
  }, [data]);

  const limit = useMemo(() => {
    if (data) {
      return data.partnerAccountLimits.achLimits;
    }
    return {};
  }, [data]);

  // Calculate remaining wire limits, ensuring values are never negative
  const wireLimitsRemains = useMemo(() => {
    if (data) {
      const { wireLimits, wireTotalsDaily, wireTotalsMonthly } = data.partnerAccountLimits;
      return {
        dailyWireRemain: Math.max(0, wireLimits.daily - wireTotalsDaily),
        monthlyWireRemain: Math.max(0, wireLimits.monthly - wireTotalsMonthly)
      };
    }
    return {};
  }, [data]);

  const wireLimits = useMemo(() => {
    if (data) {
      return data.partnerAccountLimits.wireLimits;
    }
    return {};
  }, [data]);

  const wireOutgoingFee = useMemo(() => {
    if (data) {
      return data.partnerAccountLimits.wireOutgoingFee;
    }
    return null;
  }, [data]);

  useEffect(() => {
    if (error) {
      createCommonErrorToast({ error });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  return {
    limit,
    remains,
    loading,
    error,
    wireOutgoingFee,
    wireLimitsRemains,
    wireLimits,
    refetch
  };
}
